/**
 * Unified States List for Context Panel
 * 
 * Displays artifact states for both Prime and Company artifacts.
 * Fetches from appropriate API endpoints based on artifact type.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { EnhancedArtifact } from '../../../types/artifacts';
import { PrimeArtifactService } from '../../../services/primeArtifactService';
import { StateService } from '../../../services/api/stateService';

interface UnifiedStatesListProps {
  artifact: EnhancedArtifact;
  isPrime?: boolean;
  onStateClick?: (stateId: string) => void;
}

export const UnifiedStatesList: React.FC<UnifiedStatesListProps> = ({
  artifact,
  isPrime,
  onStateClick
}) => {
  const [states, setStates] = useState<any[]>(artifact.states || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-detect if prime artifact
  const isPrimeArtifact = isPrime ?? (
    !artifact.artifact_tree_id || artifact.prime_artifact_id === artifact.id
  );

  const fetchStates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let fetchedStates;
      
      if (isPrimeArtifact) {
        // Fetch prime artifact states
        fetchedStates = await PrimeArtifactService.getPrimeArtifactStates(artifact.id);
      } else {
        // Fetch company artifact states
        fetchedStates = await StateService.getStatesByArtifact(artifact.id as any);
      }

      setStates(fetchedStates);
    } catch (err) {
      console.error('Failed to fetch states:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch states');
    } finally {
      setLoading(false);
    }
  }, [artifact.id, isPrimeArtifact]);

  useEffect(() => {
    // Only fetch if we don't have states already
    if (!states || states.length === 0) {
      void fetchStates();
    }
  }, []);

  const handleRefresh = () => {
    void fetchStates();
  };

  if (loading && states.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '16px',
        color: '#6b7280',
        fontSize: '12px'
      }}>
        Loading states...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontSize: '12px' }}>
        <div style={{
          padding: '8px',
          backgroundColor: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '4px',
          color: '#991b1b',
          fontSize: '11px',
          marginBottom: '8px'
        }}>
          {error}
        </div>
        <button
          onClick={handleRefresh}
          style={{
            padding: '4px 12px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '11px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <ReloadOutlined style={{ fontSize: '12px' }} />
          Retry
        </button>
      </div>
    );
  }

  if (states.length === 0) {
    return (
      <div style={{ fontSize: '12px' }}>
        <div style={{ color: '#6b7280', fontStyle: 'italic', marginBottom: '8px' }}>
          No states defined
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          style={{
            padding: '4px 12px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '11px',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: loading ? 0.7 : 1
          }}
        >
          <ReloadOutlined style={{ fontSize: '12px' }} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ fontSize: '12px' }}>
      {/* Header with refresh button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '8px'
      }}>
        <span style={{ fontSize: '11px', color: '#6b7280' }}>
          {states.length} state{states.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={handleRefresh}
          disabled={loading}
          style={{
            padding: '2px 8px',
            backgroundColor: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: '3px',
            fontSize: '10px',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <ReloadOutlined style={{ fontSize: '10px' }} />
          Refresh
        </button>
      </div>

      {/* States List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {states.map(state => (
          <div
            key={state.id}
            onClick={() => onStateClick?.(state.id)}
            style={{
              padding: '8px 10px',
              backgroundColor: '#f9fafb',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
              fontSize: '12px',
              cursor: onStateClick ? 'pointer' : 'default',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (onStateClick) {
                e.currentTarget.style.backgroundColor = '#f0f9ff';
                e.currentTarget.style.borderColor = '#3b82f6';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            <div style={{ 
              fontWeight: '500', 
              color: '#374151', 
              marginBottom: '2px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              {state.name}
              {state.is_initial && (
                <span style={{
                  padding: '1px 4px',
                  backgroundColor: '#dcfce7',
                  color: '#166534',
                  borderRadius: '3px',
                  fontSize: '9px',
                  fontWeight: '600'
                }}>
                  INITIAL
                </span>
              )}
              {state.is_final && (
                <span style={{
                  padding: '1px 4px',
                  backgroundColor: '#fee2e2',
                  color: '#991b1b',
                  borderRadius: '3px',
                  fontSize: '9px',
                  fontWeight: '600'
                }}>
                  FINAL
                </span>
              )}
              {state.is_improvement && (
                <span style={{
                  padding: '1px 4px',
                  backgroundColor: '#fef3c7',
                  color: '#92400e',
                  borderRadius: '3px',
                  fontSize: '9px',
                  fontWeight: '600'
                }}>
                  IMPROVEMENT
                </span>
              )}
            </div>
            <div style={{ fontSize: '10px', color: '#9ca3af' }}>
              {state.state_type || 'Unknown Type'}
              {state.is_required && ' • Required'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

